import { useTranslation } from 'react-i18next';
import klaxMascotVideoLight from 'assets/images/magnific_mascott-t-light.mp4';
import klaxMascotVideoDark from 'assets/images/magnific_mascott-t.mp4';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { useEffect, useRef } from 'react';

export interface KlaxWelcomeProps {
  className?: string;
  title?: string;
}

export function KlaxWelcome({ className, title }: KlaxWelcomeProps) {
  const { t } = useTranslation();
  const welcomeTitle = title ?? t('WELCOME_TO_KLAX');
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const klaxMascotVideo = isDark ? klaxMascotVideoDark : klaxMascotVideoLight;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;

    try {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        void playPromise.catch(() => {
          // Autoplay may be blocked; keep the first frame visible.
        });
      }
    } catch {
      // Autoplay may not be supported in environment
    }
  }, [klaxMascotVideo]);

  const handleVideoEnded = () => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;
    video.pause();
  };

  return (
    <div className={cn('flex h-auto min-h-16 items-center justify-center gap-2 text-center sm:h-20 sm:gap-2.5', className)}>
      <div className="size-[50px] shrink-0 overflow-hidden rounded-full">
        <video
          ref={videoRef}
          key={resolvedTheme}
          src={klaxMascotVideo}
          muted
          playsInline
          preload="auto"
          aria-hidden
          onEnded={handleVideoEnded}
          className={cn(
            'size-full object-cover',
            isDark ? 'scale-[1.30] mix-blend-screen' : 'scale-[1.30]'
          )}
          width={50}
          height={50}
        />
      </div>

      <p
        className={cn(
          'text-center font-heyJune min-w-0 text-[26px] font-normal leading-[1.1] xs:text-3xl md:text-[36px] xl:text-[36px]',
          isDark ? 'text-true-white' : 'text-grey-950'
        )}
      >
        {title ?? t('WELCOME_TO_KLAX', 'Welcome to klax!')}
      </p>
    </div>
  );
}
